<template>
  <div style="min-height:200px" class="vx-col mt-3">

    <vs-popup style="z-index:99999999;" v-if="inSpaceCreationMode" :active.sync="inSpaceCreationMode" title="Create subspace">
      <CreateSpace v-if="inSpaceCreationMode" :isSubspaceCreation="true" :parentId="currentSpaceId" @space-creation-completed="handleSubspaceCreation"></CreateSpace>
    </vs-popup>

    <!-- Search Input -->
    <div class="search-container mb-3">
      <vs-input 
        v-model="searchTerm" 
        placeholder="Search spaces..." 
        
        @input="filterTree"
        class="w-full"
      />
    </div>

    <!-- Tree View -->
    <div class="tree-container" style="max-height: 400px; overflow-y: auto; border: 1px solid #e0e0e0; border-radius: 4px;">
      <LiquorTree
        v-if="filteredTreeData.length > 0"
        :key="searchTerm + '_' + filteredTreeData.length"
        :data="filteredTreeData"
        ref="spaceTree"
        :options="treeOptions"
        @node:selected="onNodeSelected"
        @node:deselected="onNodeDeselected"
        @node:checked="onNodeChecked"
        @node:unchecked="onNodeUnchecked"
        style="min-height: 300px;"
      >
        <div slot-scope="{ node }" class="node-container">
          <div>
            <vs-icon icon-pack="feather"
                     :icon="getNodeIcon(node)"
                     size="small"
                     class="node-icon" />
            <span class="node-text">{{ node.text }}</span>
          </div>
          <div class="actions">
            <FeatherIcon @click.stop="openSpaceCreationMenu(node)" title="Add subspace" icon="PlusIcon" ></FeatherIcon>
            <FeatherIcon @click.stop="deleteSpace(node)" class="delete" title="Delete space" icon="TrashIcon" ></FeatherIcon>
          </div>
        </div>
      </LiquorTree>
      
      <!-- No spaces found -->
      <div v-else class="no-data d-flex align-items-center justify-content-center" style="min-height: 300px;">
        <div class="text-center">
          <vs-icon icon-pack="feather" icon="icon-folder-x" size="large" class="mb-3" />
          <h6>No spaces found</h6>
        </div>
      </div>
    </div>

    <!-- Selected Spaces Display -->
    <div v-if="selectedSpaces.length > 0" class="selected-spaces mt-3" style="display: none;">
      <h6>Currently in Spaces:</h6>
      <div class="selected-space-list">
        <vs-chip 
          v-for="space in selectedSpaces" 
          :key="space.id"
          closable
          @click="removeSpace(space)"
          class="mr-2 mb-2"
        >
          {{ space.title }}
        </vs-chip>
      </div>
    </div>

    <!-- Pending Selections Display -->
    <div v-if="false" class="pending-selections mt-3" style="display: none;">
      <h6>Pending Additions:</h6>
      <div class="pending-space-list">
        <vs-chip 
          v-for="space in []" 
          :key="space.id"
          closable
          @click="removePendingSpace(space)"
          class="mr-2 mb-2"
          color="primary"
        >
          {{ space.title }}
        </vs-chip>
      </div>
    </div>
  </div>
</template>

<script>
  import LiquorTree from 'liquor-tree';
  import TCVelikClient from '@/TCVelikClient/TCVelikClient';
  import FeatherIcon from "@/components/FeatherIcon.vue";
  import CreateSpace from '@/views/components/spaces/CreateSpace.vue'

  export default {
    props: ["onboarding_id"],
    data() {
      return {
        currentNode:null,
        currentSpaceId: null,
        inSpaceCreationMode:false,
        treeData: [],
        filteredTreeData: [],
        searchTerm: '',
        selectedSpaces: [],
        treeOptions: {
          checkbox: true,
          multiple: true,
          expandable: true, // Enable expandable functionality
          autoCheckChildren: false // Disable auto-checking of children
        },
        allSpaces: [] // Store all spaces for search
      }
    },

    watch: {
      filteredTreeData: {
        handler(newData) {
          // LiquorTree should automatically update when data changes
          // No need to call setData method
        },
        deep: true
      }
    },

    async mounted() {
      // First load current spaces to know which ones are selected
      await this.LoadCurrentSpaces();
      // Then load the full hierarchy with proper checkbox states
      await this.LoadSpacesHierarchy();
    },

    methods: {
      async LoadSpacesHierarchy() {
        try {
          // Load ALL spaces in flat structure
          const query = `
          query LoadSpacesHierarchy($onboardingId: Int!) {
            loadSpaces(onboardingId: $onboardingId) {
        id
        title
        description
        icon
        urlName
        parentId
              rootId
              ordinalPosition
              hideFromSidebar
              subscriptionId
              paymentLink
              certificateTitle
              certificateIcon
              certificateDescription
            }
          }
        `;

          const variables = {
            onboardingId: this.onboarding_id * 1,
          };

          const req = {
            query,
            variables,
          };

          const response = await TCVelikClient.PostRelative("/api/graphql", req, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            }
          });

          if (response && response.data.data) {
            if (response.data.data.loadSpaces && Array.isArray(response.data.data.loadSpaces)) {
              const allSpaces = response.data.data.loadSpaces;

              // Build hierarchy from flat list
              const rootSpaces = this.buildSpaceHierarchy(allSpaces);

              this.treeData = this.transformSpacesToTreeData(rootSpaces);
              this.filteredTreeData = [...this.treeData];
              this.allSpaces = allSpaces; // Keep flat list for search
            } else {
              this.treeData = [];
              this.filteredTreeData = [];
            }
          } else {
            this.treeData = [];
            this.filteredTreeData = [];
          }
        } catch (error) {
          this.$vs.notify({
            title: "Error",
            text: "Failed to load spaces",
            color: "danger",
            iconPack: "feather",
            position: "top-center",
            icon: "icon-alert-circle",
          });
        }
      },

      toggleSpace(space) {
        if (space.state.checked) {
          if (!this.selectedSpaces.some(s => s.id === space.data.id)) {
            this.selectedSpaces.push(space.data);
            this.AddOnboardingToSpace(space.data);
          }
        } else {
          const index = this.selectedSpaces.findIndex(s => s.id === space.data.id);
          if (index > -1) {
            this.selectedSpaces.splice(index, 1);
            this.RemoveOnboardingFromSpace(space.data);
          }
        }
      },

      async LoadCurrentSpaces() {
        try {
          const query = `
          mutation LoadSpaces($onboardingId: Int!) {
            loadSpaces(onboardingId: $onboardingId) {
      id
      title
      description
      icon
      urlName
      parentId
              rootId
              ordinalPosition
              hideFromSidebar
      subscriptionId
      paymentLink
              certificateTitle
              certificateIcon
              certificateDescription
    }
  }
`;

          const variables = {
            onboardingId: this.onboarding_id * 1,
          };

          const req = {
            query,
            variables,
          };

          const response = await TCVelikClient.PostRelative("/api/graphql", req, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            }
          });

          if (response && response.data.data) {
            this.selectedSpaces = response.data.data.loadSpaces;
            this.updateTreeSelection();
          }
        } catch (error) {
          // Error loading current spaces - continue without them
        }
      },

      buildSpaceHierarchy(allSpaces) {
        // Create a map for quick lookup
        const spaceMap = new Map();
        allSpaces.forEach(space => {
          spaceMap.set(space.id, { ...space, children: [] });
        });

        // Build hierarchy
        const rootSpaces = [];
        allSpaces.forEach(space => {
          const spaceWithChildren = spaceMap.get(space.id);
          if (space.parentId === null) {
            rootSpaces.push(spaceWithChildren);
          } else {
            const parent = spaceMap.get(space.parentId);
            if (parent) {
              parent.children.push(spaceWithChildren);
            }
          }
        });

        return rootSpaces;
      },

      transformSpacesToTreeData(spaces) {
        return spaces.map(space => {
          const isChecked = this.selectedSpaces.some(s => s.id === space.id);

          const node = {
            text: space.title,
            data: space,
            children: space.children && space.children.length > 0 ? this.transformSpacesToTreeData(space.children) : [],
            state: {
              selected: false,
              selectable: true,
              checked: isChecked,
              expanded: false,
              disabled: false,
              visible: true,
              indeterminate: false
            }
          };

          return node;
        });
      },

      transformNewlyCreatedSubspaceToNode(space) {
        const node = {
          text: space.title,
          data: space,
          children: [],
          state: {
            selected: false,
            selectable: true,
            checked: false,
            expanded: false,
            disabled: false,
            visible: true,
            indeterminate: false
          }
        }
        return node;
      },

      openSpaceCreationMenu(node) {
        this.currentNode = node
        this.currentSpaceId = node.data.id
        this.inSpaceCreationMode = true
      },


      deleteSpace(node) {
        var spaceId = node.data.id
        if (confirm("Are you sure?The SOPs in this space will still be available in the 'Discover' section.")) {

        } else {
          return;
        }
        var instance = this
        const query = `
    mutation DeleteSpace($spaceId:Int!) {
    deleteSpace(spaceId: $spaceId)
 }
`;
        const variables = {
          spaceId: spaceId
        }

        const req = {
          query,
          variables
        };
        TCVelikClient.PostRelative("/api/graphql", req, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }
        }).then(function (response) {
          if (response && response.data) {

            instance.$vs.notify({
              title: "Successfully deleted space!",
              color: "success",
              iconPack: "feather",
              position: "top-center",
              icon: "icon-check-circle",
            });
            instance.$refs.spaceTree.remove(node,false)
          }
          else if (response && response.data && response.data.errors) {
            instance.$vs.notify({
              title: "A problem occured while attempting to delete this space!",
              color: "success",
              iconPack: "feather",
              position: "top-center",
              icon: "icon-check-circle",
            });
            console.error("ERROR SAVING ONBOARDING: ", response.data);
          }
        });
      },

      handleSubspaceCreation(space) {
        var node = this.transformNewlyCreatedSubspaceToNode(space)
        this.$refs.spaceTree.append(this.currentNode, node)
        this.inSpaceCreationMode = false
        this.currentNode = null
        this.currentSpaceId = null
      },

      getNodeIcon(node) {
        return node.data.icon || 'icon-folder';
      },

      isSpaceSelected(node) {
        return this.selectedSpaces.some(space => space.id === node.data.id);
      },

      onNodeSelected(node) {
        // Node selection - no action needed, only checkbox changes update pending
      },

      onNodeDeselected(node) {
        // Node deselection - no action needed, only checkbox changes update pending
      },

      onNodeChecked(node) {
        // Immediately add to space
        this.AddOnboardingToSpace(node.data);
      },

      onNodeUnchecked(node) {
        // Immediately remove from space
        this.RemoveOnboardingFromSpace(node.data);
      },

      removeSpace(space) {
        const index = this.selectedSpaces.findIndex(s => s.id === space.id);
        if (index > -1) {
          this.selectedSpaces.splice(index, 1);
          this.RemoveOnboardingFromSpace(space);
          this.updateTreeSelection();
        }
      },

      updateTreeSelection() {
        // Update tree node states based on selected spaces
        this.updateNodeStates(this.filteredTreeData);
      },

      updateNodeStates(nodes) {
        nodes.forEach(node => {
          node.state.checked = this.selectedSpaces.some(space => space.id === node.data.id);
          if (node.children && node.children.length > 0) {
            this.updateNodeStates(node.children);
          }
        });
      },

      filterTree() {
        if (!this.searchTerm.trim()) {
          this.filteredTreeData = [...this.treeData];
          return;
        }

        const searchLower = this.searchTerm.toLowerCase();

        // Simple approach: filter the existing tree structure
        this.filteredTreeData = this.filterNodes(this.treeData, searchLower);

        // LiquorTree should automatically update when filteredTreeData changes
        // No need to manually call setData
      },

      buildTreeFromSpaces(matchingSpaces) {
        // Create a map of all spaces for quick lookup
        const spaceMap = new Map();
        this.allSpaces.forEach(space => spaceMap.set(space.id, space));

        // Find root spaces that match or have matching children
        const rootSpaces = this.treeData.filter(rootSpace => {
          const space = rootSpace.data;
          const matches = matchingSpaces.some(match => match.id === space.id);

          // Check if any children match
          const hasMatchingChildren = this.hasMatchingChildren(space.id, matchingSpaces, spaceMap);

          return matches || hasMatchingChildren;
        });

        // Build the tree structure with matching nodes
        return rootSpaces.map(rootSpace => this.buildNodeWithMatches(rootSpace, matchingSpaces, spaceMap));
      },

      hasMatchingChildren(parentId, matchingSpaces, spaceMap) {
        const children = this.allSpaces.filter(space => space.parentId === parentId);
        return children.some(child =>
          matchingSpaces.some(match => match.id === child.id) ||
          this.hasMatchingChildren(child.id, matchingSpaces, spaceMap)
        );
      },

      buildNodeWithMatches(node, matchingSpaces, spaceMap) {
        const space = node.data;
        const isMatching = matchingSpaces.some(match => match.id === space.id);

        // Get children that match or have matching descendants
        const children = this.allSpaces.filter(child => child.parentId === space.id);
        const matchingChildren = children.filter(child =>
          matchingSpaces.some(match => match.id === child.id) ||
          this.hasMatchingChildren(child.id, matchingSpaces, spaceMap)
        );

        return {
          ...node,
          children: matchingChildren.map(child => {
            const childNode = this.transformSpacesToTreeData([child])[0];
            return this.buildNodeWithMatches(childNode, matchingSpaces, spaceMap);
          }),
          state: {
            ...node.state,
            expanded: isMatching || matchingChildren.length > 0 // Auto-expand if matches
          }
        };
      },

      filterNodes(nodes, searchTerm) {
        const filtered = [];

        nodes.forEach(node => {
          const nodeMatches = node.text.toLowerCase().includes(searchTerm) ||
            node.data.description?.toLowerCase().includes(searchTerm);

          let filteredChildren = [];
          if (node.children && node.children.length > 0) {
            filteredChildren = this.filterNodes(node.children, searchTerm);
          }

          // Include node if it matches or has matching children
          if (nodeMatches || filteredChildren.length > 0) {
            filtered.push({
              ...node,
              children: filteredChildren,
              state: {
                ...node.state,
                expanded: nodeMatches || filteredChildren.length > 0 // Auto-expand if matches
              }
            });
          }
        });

        return filtered;
      },

      async AddOnboardingToSpace(space) {
        try {
          const query = `
          mutation AddToSpace($onboardingId: Int!, $spaceId: Int!) {
            addToSpace(onboardingId: $onboardingId, spaceId: $spaceId) {
              onboardingId
              spaceId
            }
          }
        `;

          const variables = {
            onboardingId: this.onboarding_id * 1,
            spaceId: space.id
          };

          const req = {
            query,
            variables
          };

          const response = await TCVelikClient.PostRelative("/api/graphql", req, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            }
          });

          if (response && response.data.data) {
            // Add to selectedSpaces if not already there
            if (!this.selectedSpaces.some(s => s.id === space.id)) {
              this.selectedSpaces.push(space);
            }

            this.$vs.notify({
              title: "Successfully added onboarding to space!",
              text: `Added to "${space.title}"`,
              color: "success",
              iconPack: "feather",
              position: "top-center",
              icon: "icon-check-circle",
            });

            return true;
          } else {
            throw new Error("Failed to add onboarding to space");
          }
        } catch (error) {
          this.$vs.notify({
            title: "Error",
            text: `Failed to add to "${space.title}"`,
            color: "danger",
            iconPack: "feather",
            position: "top-center",
            icon: "icon-alert-circle",
          });
          throw error;
        }
      },

      async RemoveOnboardingFromSpace(space) {
        try {
          const query = `
          mutation RemoveFromSpace($onboardingId: Int!, $spaceId: Int!) {
            removeFromSpace(onboardingId: $onboardingId, spaceId: $spaceId)
          }
        `;

          const variables = {
            onboardingId: this.onboarding_id * 1,
            spaceId: space.id
          };

          const req = {
            query,
            variables
          };

          const response = await TCVelikClient.PostRelative("/api/graphql", req, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            }
          });

          if (response && response.data.data) {
            // Remove from selectedSpaces
            const index = this.selectedSpaces.findIndex(s => s.id === space.id);
            if (index > -1) {
              this.selectedSpaces.splice(index, 1);
            }

            this.$vs.notify({
              title: "Successfully removed onboarding from space!",
              text: `Removed from "${space.title}"`,
              color: "success",
              iconPack: "feather",
              position: "top-center",
              icon: "icon-check-circle",
            });
          } else {
            throw new Error("Failed to remove onboarding from space");
          }
        } catch (error) {
          this.$vs.notify({
            title: "Error",
            text: `Failed to remove from "${space.title}"`,
            color: "danger",
            iconPack: "feather",
            position: "top-center",
            icon: "icon-alert-circle",
          });
        }
      }
    },

    components: {
      LiquorTree,
      FeatherIcon,
      CreateSpace
    }
  }
</script>


<style lang="scss" scoped>


  .actions {
    visibility: hidden;
    margin-left:auto;
    z-index:99999;
  }

  .tree-anchor:hover .actions {
    visibility: visible;
  }

  .actions .delete {
    color: indianred;
  }

  .actions span{
      width:20px;
  }


  .vs-con-input {
    .vs-icon {
      right: 5px !important;
      left: unset !important;
    }
  }

  .vs-icon.vs-input--icon {
    right: 5px !important;
    left: unset !important;
  }

  .search-container {
    .vs-input {
      width: 100%;
    }
    // Fix search icon positioning
    :deep(.vs-input--icon) {
      right: 0px !important;
      left: auto !important;
    }

    :deep(.vs-input--icon.material-icons) {
      right: 0px !important;
      left: auto !important;
    }

    :deep(.vs-input--icon.notranslate) {
      right: 0px !important;
      left: auto !important;
    }
  }

  .tree-container {
    background: white;
    padding: 10px;
  }

  .node-container {
    width:100%;
    display: flex;
    align-items: center;
    padding: 4px 0;
    cursor: pointer;

    .node-icon {
      margin-right: 8px;
      width: 16px;
      height: 16px;
    }

    .node-text {
      flex: 1;
      font-size: 14px;
      padding-top: 8px;
      padding-left: 5px;
    }

    .selected-indicator {
      margin-left: 8px;
    }

    .loading-indicator {
      margin-left: 8px;

      .rotating {
        animation: spin 1s linear infinite;
      }
    }

    .expand-indicator {
      margin-left: 8px;
      color: #666;
      cursor: pointer;

      &:hover {
        color: #333;
      }
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  .selected-spaces, .pending-selections {
    h6 {
      margin-bottom: 10px;
      font-weight: 600;
      color: #333;
    }

    .selected-space-list, .pending-space-list {
      display: flex;
      flex-wrap: wrap;
    }
  }

  .save-actions {
    padding: 20px 0;
    border-top: 1px solid #e0e0e0;
    margin-top: 20px;
  }

  // Tree styling overrides
  :deep(.lt-tree) {
    .lt-node {
      .lt-node-content {
        padding: 4px 0;

        &:hover {
          background-color: #f8f9fa;
        }
      }

      .lt-node-checkbox {
        margin-right: 8px !important;
        width: 24px !important;
        height: 24px !important;
        margin-top: 10px !important;

        &::after {
          left: 6px !important;
          top: 2px !important;
          height: 12px !important;
          width: 9px !important;
        }
      }
      // Additional checkbox overrides
      :deep(.lt-node-checkbox) {
        width: 24px !important;
        height: 24px !important;
        margin-top: 10px !important;

        &::after {
          left: 6px !important;
          top: 2px !important;
          height: 12px !important;
          width: 9px !important;
        }
      }
    }
  }
</style>
